import { useState, useEffect, type JSX } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { InputText } from 'primereact/inputtext'
import { InputNumber } from 'primereact/inputnumber'
import { InputSwitch } from 'primereact/inputswitch'
import { Password } from 'primereact/password'
import { Dropdown } from 'primereact/dropdown'
import { api } from '../../../api/client.js'
import { useToast } from '../../../components/system/ToastProvider.js'
import EmailOAuthSignInButtons, { type EmailOAuthMessage } from '../../../components/admin/channels/EmailOAuthSignInButtons.js'
import TestEmailDrawer from '../../../components/admin/channels/TestEmailDrawer.js'
import type { EmailInboundConfig } from './EmailInboundPage.js'
import {
  FormContainer,
  FormGrid,
  FormField,
  FormDivider,
  StepperSection,
  StickyFooter,
  type StepItem,
} from '../../../components/forms/layout/index.js'

const SERVICE_PROVIDERS = [
  { label: '126', value: '126' },
  { label: '163', value: '163' },
  { label: '1und1', value: '1und1' },
  { label: 'AOL', value: 'AOL' },
  { label: 'DebugMail', value: 'DebugMail' },
  { label: 'DynectEmail', value: 'DynectEmail' },
  { label: 'FastMail', value: 'FastMail' },
  { label: 'GandiMail', value: 'GandiMail' },
  { label: 'Gmail', value: 'Gmail' },
  { label: 'Godaddy', value: 'Godaddy' },
  { label: 'GodaddyAsia', value: 'GodaddyAsia' },
  { label: 'GodaddyEurope', value: 'GodaddyEurope' },
  { label: 'HotEe', value: 'HotEe' },
  { label: 'Hotmail', value: 'Hotmail' },
  { label: 'iCloud', value: 'iCloud' },
  { label: 'MailEe', value: 'MailEe' },
  { label: 'MailRu', value: 'MailRu' },
  { label: 'Maildev', value: 'Maildev' },
  { label: 'Mailgun', value: 'Mailgun' },
  { label: 'Mailjet', value: 'Mailjet' },
  { label: 'Mailosaur', value: 'Mailosaur' },
  { label: 'Mandrill', value: 'Mandrill' },
  { label: 'Naver', value: 'Naver' },
  { label: 'OpenMailBox', value: 'OpenMailBox' },
  { label: 'Outlook365', value: 'Outlook365' },
  { label: 'Postmark', value: 'Postmark' },
  { label: 'QQ', value: 'QQ' },
  { label: 'QQex', value: 'QQex' },
  { label: 'SendCloud', value: 'SendCloud' },
  { label: 'SendGrid', value: 'SendGrid' },
  { label: 'SendinBlue', value: 'SendinBlue' },
  { label: 'SendPulse', value: 'SendPulse' },
  { label: 'SES', value: 'SES' },
  { label: 'SesUsEast_1', value: 'SesUsEast_1' },
  { label: 'SesUsWest_2', value: 'SesUsWest_2' },
  { label: 'SesEuWest_1', value: 'SesEuWest_1' },
  { label: 'Sparkpost', value: 'Sparkpost' },
  { label: 'Yahoo', value: 'Yahoo' },
  { label: 'Yandex', value: 'Yandex' },
  { label: 'Zoho', value: 'Zoho' },
  { label: 'QiyeAliyun', value: 'QiyeAliyun' },
]

export default function EmailInboundFormPage(): JSX.Element {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isViewMode = searchParams.get('view') === 'true'
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [activeTab, setActiveTab] = useState(0)
  const [testDrawerOpen, setTestDrawerOpen] = useState(false)
  const [campaigns, setCampaigns] = useState<{ label: string; value: string }[]>([])
  // Secrets are never returned by the API; these flags say a value is already stored.
  const [stored, setStored] = useState({ clientSecret: false, accessToken: false, refreshToken: false })
  const [formData, setFormData] = useState({
    status: true,
    isDefault: false,
    criticalGw: false,
    schedulerGw: false,
    name: '',
    description: '',
    campaignId: '',
    gatewayProviderEnabled: false,
    imapHost: '',
    imapPort: 993,
    authEnabled: false,
    serviceProvider: '',
    oauthProvider: '',
    imapUser: '',
    imapPassword: '',
    clientId: '',
    clientSecret: '',
    accessToken: '',
    refreshToken: '',
    connectionSecurity: true,
  })

  const isEditMode = id && id !== 'new' && !isViewMode

  useEffect(() => {
    if (id && id !== 'new') {
      void fetchConfig()
    }
  }, [id])

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.get<{ campaigns: { id: string; name: string }[] }>('/campaigns?limit=200')
        setCampaigns(data.campaigns.map((c) => ({ label: c.name, value: c.id })))
      } catch {
        // Non-fatal: the mailbox still saves, conversations just carry no default campaign.
        setCampaigns([])
      }
    })()
  }, [])

  const fetchConfig = async () => {
    setLoading(true)
    try {
      const data = await api.get<EmailInboundConfig>(`/admin/channels/email/inbound/${id}`)
      setStored({
        clientSecret: !!data.hasClientSecret,
        accessToken: !!data.hasAccessToken,
        refreshToken: !!data.hasRefreshToken,
      })
      setFormData({
        status: data.status === 'ACTIVE',
        isDefault: data.isDefault,
        criticalGw: false,
        schedulerGw: false,
        name: data.name || '',
        description: data.description || '',
        campaignId: data.campaignId || '',
        gatewayProviderEnabled: !!data.imapHost,
        imapHost: data.imapHost || '',
        imapPort: data.imapPort || 993,
        authEnabled: data.authEnabled || false,
        serviceProvider: '',
        oauthProvider: data.oauthProvider || '',
        imapUser: data.imapUser,
        imapPassword: '',
        clientId: data.clientId || '',
        clientSecret: '',
        accessToken: '',
        refreshToken: '',
        connectionSecurity: data.connectionSecurity ?? true,
      })
    } catch {
      toast.error('Failed to fetch email inbound config')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Client-side validation
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.gatewayProviderEnabled && !formData.serviceProvider.trim()) {
      newErrors.serviceProvider = 'Service Provider is required'
    }

    if (formData.gatewayProviderEnabled && !formData.imapHost.trim()) {
      newErrors.imapHost = 'Host is required'
    }

    if (!formData.imapUser.trim()) {
      newErrors.imapUser = 'Username is required'
    }

    if (!isEditMode && !formData.imapPassword.trim()) {
      newErrors.imapPassword = 'Password is required'
    }

    if (formData.authEnabled && !formData.clientId.trim()) {
      newErrors.clientId = 'Client ID is required'
    }

    if (formData.authEnabled && !formData.clientSecret.trim() && !stored.clientSecret) {
      newErrors.clientSecret = 'Client Secret is required'
    }

    if (formData.authEnabled && !formData.refreshToken.trim() && !stored.refreshToken) {
      newErrors.refreshToken = 'Refresh Token is required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      if (newErrors.name) setActiveTab(0)
      else setActiveTab(1)
      toast.error('Please fix the validation errors')
      return
    }

    setErrors({})
    setLoading(true)
    try {
      const payload: Record<string, unknown> = {
        status: formData.status ? 'ACTIVE' : 'DISABLED',
        isDefault: formData.isDefault,
        name: formData.name,
        description: formData.description || null,
        campaignId: formData.campaignId || null,
        imapHost: formData.gatewayProviderEnabled ? formData.imapHost : null,
        imapPort: formData.gatewayProviderEnabled ? formData.imapPort : 993,
        imapUser: formData.imapUser,
        authEnabled: formData.authEnabled,
        connectionSecurity: formData.connectionSecurity,
        mailboxName: 'INBOX',
        pollIntervalSeconds: 30,
        useIdle: false,
        oauthProvider: formData.authEnabled ? formData.oauthProvider || null : null,
        clientId: formData.authEnabled ? formData.clientId || null : null,
      }
      // Blank secret = unchanged (omitted); switching OAuth off clears them.
      if (!formData.authEnabled) {
        payload.clientSecret = null
        payload.accessToken = null
        payload.refreshToken = null
      } else {
        if (formData.clientSecret) payload.clientSecret = formData.clientSecret
        if (formData.accessToken) payload.accessToken = formData.accessToken
        if (formData.refreshToken) payload.refreshToken = formData.refreshToken
      }
      if (formData.imapPassword) {
        payload.imapPassword = formData.imapPassword
      }

      if (isEditMode) {
        await api.put(`/admin/channels/email/inbound/${id}`, payload)
        toast.success('Email inbound config updated')
      } else {
        await api.post('/admin/channels/email/inbound', payload)
        toast.success('Email inbound config created')
      }
      navigate('/admin/channels/email/inbound')
    } catch {
      toast.error('Failed to save email inbound config')
    } finally {
      setLoading(false)
    }
  }

  const validateField = (field: string, value: string): string | null => {
    if (field === 'name' && !value.trim()) return 'Name is required'
    if (field === 'serviceProvider' && !formData.gatewayProviderEnabled && !value.trim()) return 'Service Provider is required'
    if (field === 'imapUser' && !value.trim()) return 'Username is required'
    if (field === 'imapPassword' && !isEditMode && !value.trim()) return 'Password is required'
    if (field === 'imapHost' && formData.gatewayProviderEnabled && !value.trim()) return 'Host is required'
    if (field === 'clientId' && formData.authEnabled && !value.trim()) return 'Client ID is required'
    if (field === 'clientSecret' && formData.authEnabled && !value.trim() && !stored.clientSecret) return 'Client Secret is required'
    if (field === 'refreshToken' && formData.authEnabled && !value.trim() && !stored.refreshToken) return 'Refresh Token is required'
    return null
  }

  const handleFieldChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value })
    const error = validateField(field, value)
    setErrors((prev) => ({ ...prev, [field]: error || '' }))
  }

  const handleCancel = () => {
    navigate('/admin/channels/email/inbound')
  }

  const canTestConnection =
    formData.gatewayProviderEnabled &&
    formData.imapHost.trim() !== '' &&
    formData.imapPort > 0 &&
    formData.imapUser.trim() !== '' &&
    (formData.imapPassword.trim() !== '' || isEditMode)

  const handleOAuthComplete = (result: EmailOAuthMessage) => {
    if (id === 'new') {
      navigate(`/admin/channels/email/inbound/${result.configId}`)
    } else {
      void fetchConfig()
    }
    toast.success(`${result.provider === 'google' ? 'Google' : 'Microsoft'} account connected`)
  }

  const title = isViewMode ? 'View Email Inbound Config' : isEditMode ? 'Edit Email Inbound Config' : 'Add Email Inbound Config'
  const description = isViewMode
    ? 'View IMAP inbound email configuration details.'
    : isEditMode
      ? 'Edit IMAP inbound email configuration.'
      : 'Configure a new IMAP inbound email provider.'

  if (loading && id && id !== 'new') {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="pi pi-spinner pi-spin text-3xl text-[var(--kc3-accent)]" />
          <span className="text-sm font-medium text-gray-600">Loading email inbound config...</span>
        </div>
      </div>
    )
  }

  const bodyClass = `space-y-5 p-6 ${isViewMode ? 'pointer-events-none' : ''}`

  const panelHeader = (icon: string, eyebrow: string, headingTitle: string, headingDescription: string): JSX.Element => (
    <div className="border-b border-gray-100 px-6 py-5">
      <div className="flex items-start gap-3.5">
        <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--kc3-accent)]/10 text-[var(--kc3-accent)]">
          <i className={`${icon} text-lg`} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--kc3-accent)]">{eyebrow}</span>
          <h2 className="mt-0.5 text-lg font-bold text-gray-900">{headingTitle}</h2>
          <p className="mt-1 text-sm text-gray-500">{headingDescription}</p>
        </div>
      </div>
    </div>
  )

  const tabs: StepItem[] = [
    {
      key: 'basic',
      step: 1,
      label: 'Basic Info',
      sublabel: 'Name & Flags',
      content: (
        <div className="rounded-sm border border-gray-200 bg-white shadow-sm overflow-hidden">
          {panelHeader('pi pi-envelope', 'BASIC INFO', 'Basic Information', 'Name, description, and gateway flags.')}
          <div className={bodyClass}>
            <FormGrid columns={3}>
              <FormField label="Status" htmlFor="status">
                <div className="flex items-center gap-2">
                  <InputSwitch
                    inputId="status"
                    checked={formData.status}
                    onChange={(e: { value?: boolean }) => setFormData({ ...formData, status: e.value ?? false })}
                    disabled={isViewMode}
                  />
                  <span className="text-sm text-gray-600">{formData.status ? 'Active' : 'Inactive'}</span>
                </div>
              </FormField>
              <FormField label="Is Default" htmlFor="isDefault">
                <InputSwitch
                  inputId="isDefault"
                  checked={formData.isDefault}
                  onChange={(e: { value?: boolean }) => setFormData({ ...formData, isDefault: e.value ?? false })}
                  disabled={isViewMode}
                />
              </FormField>
              <FormField label="Critical GW" htmlFor="criticalGw">
                <InputSwitch
                  inputId="criticalGw"
                  checked={formData.criticalGw}
                  onChange={(e: { value?: boolean }) => setFormData({ ...formData, criticalGw: e.value ?? false })}
                  disabled={isViewMode}
                />
              </FormField>
              <FormField label="Scheduler GW" htmlFor="schedulerGw">
                <InputSwitch
                  inputId="schedulerGw"
                  checked={formData.schedulerGw}
                  onChange={(e: { value?: boolean }) => setFormData({ ...formData, schedulerGw: e.value ?? false })}
                  disabled={isViewMode}
                />
              </FormField>
            </FormGrid>

            <FormDivider />

            <FormGrid columns={2}>
              <FormField label="Name" htmlFor="name" required error={errors.name}>
                <InputText
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  className={`w-full ${errors.name ? 'p-invalid' : ''}`}
                  disabled={isViewMode}
                />
              </FormField>
              <FormField label="Description" htmlFor="description">
                <InputText
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full"
                  disabled={isViewMode}
                />
              </FormField>
              <FormField
                label="Campaign"
                htmlFor="campaignId"
                help="Conversations created from this mailbox are opened under this campaign. A routing rule that sets a campaign overrides it."
              >
                <Dropdown
                  inputId="campaignId"
                  value={formData.campaignId}
                  onChange={(e) => setFormData({ ...formData, campaignId: (e.value as string) ?? '' })}
                  options={campaigns}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="No campaign"
                  showClear
                  filter
                  className="w-full"
                  disabled={isViewMode}
                />
              </FormField>
            </FormGrid>
          </div>
        </div>
      ),
    },
    {
      key: 'gateway',
      step: 2,
      label: 'Gateway Information',
      sublabel: 'Connection & Auth',
      content: (
        <div className="rounded-sm border border-gray-200 bg-white shadow-sm overflow-hidden">
          {panelHeader('pi pi-server', 'GATEWAY', 'Gateway Information', 'Connection host, authentication, and credentials.')}
          <div className={bodyClass}>
            <FormGrid columns={2}>
              <FormField label="Gateway Provider" htmlFor="gatewayProviderEnabled">
                <InputSwitch
                  inputId="gatewayProviderEnabled"
                  checked={formData.gatewayProviderEnabled}
                  onChange={(e: { value?: boolean }) => setFormData({ ...formData, gatewayProviderEnabled: e.value ?? false })}
                  disabled={isViewMode}
                />
              </FormField>
              {!formData.gatewayProviderEnabled && (
                <FormField label="Service Provider" htmlFor="serviceProvider" required error={errors.serviceProvider}>
                  <Dropdown
                    inputId="serviceProvider"
                    value={formData.serviceProvider}
                    onChange={(e) => handleFieldChange('serviceProvider', e.value as string)}
                    options={SERVICE_PROVIDERS}
                    optionLabel="label"
                    optionValue="value"
                    className={`w-full ${errors.serviceProvider ? 'p-invalid' : ''}`}
                    disabled={isViewMode}
                  />
                </FormField>
              )}
            </FormGrid>

            <FormDivider />

            <FormField label="Auth" htmlFor="authEnabled">
              <InputSwitch
                inputId="authEnabled"
                checked={formData.authEnabled}
                onChange={(e: { value?: boolean }) => setFormData({ ...formData, authEnabled: e.value ?? false })}
                disabled={isViewMode}
              />
            </FormField>

            {formData.authEnabled && !isViewMode && (
              <FormField label="Sign in with" htmlFor="oauth">
                <EmailOAuthSignInButtons configId={id} channel="inbound" onOAuthComplete={handleOAuthComplete} />
              </FormField>
            )}

            {formData.authEnabled && (
              <FormGrid columns={2}>
                <FormField label="Client ID" htmlFor="clientId" required error={errors.clientId}>
                  <InputText
                    id="clientId"
                    value={formData.clientId}
                    onChange={(e) => handleFieldChange('clientId', e.target.value)}
                    className={`w-full ${errors.clientId ? 'p-invalid' : ''}`}
                    placeholder="Client ID"
                    disabled={isViewMode}
                  />
                </FormField>
                <FormField label="Client Secret" htmlFor="clientSecret" required error={errors.clientSecret}>
                  <Password
                    inputId="clientSecret"
                    value={formData.clientSecret}
                    onChange={(e) => handleFieldChange('clientSecret', e.target.value)}
                    className={`w-full ${errors.clientSecret ? 'p-invalid' : ''}`}
                    inputClassName="w-full"
                    placeholder={stored.clientSecret ? 'Saved - leave blank to keep' : 'Client Secret'}
                    feedback={false}
                    toggleMask
                    disabled={isViewMode}
                  />
                </FormField>
                <FormField label="Access Token" htmlFor="accessToken">
                  <InputText
                    id="accessToken"
                    value={formData.accessToken}
                    onChange={(e) => setFormData({ ...formData, accessToken: e.target.value })}
                    className="w-full"
                    placeholder={stored.accessToken ? 'Saved - leave blank to keep' : 'Access Token'}
                    disabled={isViewMode}
                  />
                </FormField>
                <FormField label="Refresh Token" htmlFor="refreshToken" required error={errors.refreshToken}>
                  <InputText
                    id="refreshToken"
                    value={formData.refreshToken}
                    onChange={(e) => handleFieldChange('refreshToken', e.target.value)}
                    className={`w-full ${errors.refreshToken ? 'p-invalid' : ''}`}
                    placeholder={stored.refreshToken ? 'Saved - leave blank to keep' : 'Refresh Token'}
                    disabled={isViewMode}
                  />
                </FormField>
              </FormGrid>
            )}

            {formData.gatewayProviderEnabled && (
              <>
                <FormDivider />
                <FormGrid columns={2}>
                  <FormField label="Host" htmlFor="imapHost" required error={errors.imapHost}>
                    <InputText
                      id="imapHost"
                      value={formData.imapHost}
                      onChange={(e) => handleFieldChange('imapHost', e.target.value)}
                      className={`w-full ${errors.imapHost ? 'p-invalid' : ''}`}
                      placeholder="Host Server. eg. smtp.server.com"
                      disabled={isViewMode}
                    />
                  </FormField>
                  <FormField label="Port" htmlFor="imapPort" required>
                    <InputNumber
                      inputId="imapPort"
                      value={formData.imapPort}
                      onChange={(e) => setFormData({ ...formData, imapPort: e.value ?? 993 })}
                      className="w-full"
                      min={1}
                      max={65535}
                      disabled={isViewMode}
                    />
                  </FormField>
                </FormGrid>
                <FormField label="Connection Security" htmlFor="connectionSecurity">
                  <InputSwitch
                    inputId="connectionSecurity"
                    checked={formData.connectionSecurity}
                    onChange={(e: { value?: boolean }) => setFormData({ ...formData, connectionSecurity: e.value ?? false })}
                    disabled={isViewMode}
                  />
                </FormField>
              </>
            )}

            <FormDivider />

            <FormField label="Username" htmlFor="imapUser" required error={errors.imapUser}>
              <InputText
                id="imapUser"
                value={formData.imapUser}
                onChange={(e) => handleFieldChange('imapUser', e.target.value)}
                className={`w-full ${errors.imapUser ? 'p-invalid' : ''}`}
                disabled={isViewMode}
              />
            </FormField>

            {!formData.authEnabled && (
              <FormField
                label="Password"
                htmlFor="imapPassword"
                required={!isEditMode}
                error={errors.imapPassword}
                help={isEditMode ? 'Leave blank to keep current password.' : undefined}
              >
                <Password
                  inputId="imapPassword"
                  value={formData.imapPassword}
                  onChange={(e) => handleFieldChange('imapPassword', e.target.value)}
                  className={`w-full ${errors.imapPassword ? 'p-invalid' : ''}`}
                  inputClassName="w-full"
                  feedback={false}
                  toggleMask
                  disabled={isViewMode}
                />
              </FormField>
            )}
          </div>
        </div>
      ),
    },
  ]

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <FormContainer className="w-full px-6 py-8">
        <div className="rounded-sm border border-gray-200 bg-white shadow-sm p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <span className="inline-flex items-center rounded-full bg-[var(--kc3-accent)]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--kc3-accent)]">
                EMAIL INBOUND
              </span>
              <h1 className="mt-2 text-2xl font-bold tracking-tight text-gray-900">{title}</h1>
              <p className="mt-1 text-sm text-gray-500">{description}</p>
            </div>
            <div className="flex shrink-0 gap-3">
              {!isViewMode && canTestConnection && (
                <button
                  type="button"
                  onClick={() => setTestDrawerOpen(true)}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-5 py-2.5 text-sm font-semibold text-indigo-700 transition-all hover:bg-indigo-100 disabled:cursor-not-allowed disabled:bg-gray-100"
                >
                  <i className="pi pi-plug" aria-hidden="true" />
                  Test Connection
                </button>
              )}
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100"
              >
                {isViewMode ? 'Close' : 'Cancel'}
              </button>
              {!isViewMode && (
                <button
                  type="submit"
                  form="email-inbound-form"
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-lg bg-[var(--kc3-accent)] px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                >
                  {loading ? (
                    <>
                      <i className="pi pi-spinner pi-spin" aria-hidden="true" />
                      Saving…
                    </>
                  ) : isEditMode ? (
                    'Update'
                  ) : (
                    'Create'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        <form id="email-inbound-form" onSubmit={handleSubmit}>
          <StepperSection steps={tabs} activeIndex={activeTab} onChange={setActiveTab} />
        </form>

        {!isViewMode && (
          <StickyFooter
            icon="pi pi-envelope"
            eyebrow="You are editing"
            title={formData.name || title}
            closeLabel="Close"
            onClose={handleCancel}
            loading={loading}
          />
        )}

        <TestEmailDrawer
          visible={testDrawerOpen}
          onHide={() => setTestDrawerOpen(false)}
          mode="imap"
          getDraftConfig={() => {
            const config: Record<string, unknown> = {
              id,
              imapHost: formData.imapHost,
              imapPort: formData.imapPort,
              imapUser: formData.imapUser,
              connectionSecurity: formData.connectionSecurity,
            }
            // Only include password if it's not empty (for new configs or when user entered a new password)
            if (formData.imapPassword.trim()) {
              config.imapPassword = formData.imapPassword
            }
            return config
          }}
          testEndpoint="/admin/channels/email/inbound/test"
        />
      </FormContainer>
    </div>
  )
}
